import axios from "axios";
import {
  Industry,
  CreateIndustryData,
  UpdateIndustryData,
  IndustriesResponse,
  IndustryResponse,
  SimpleResponse,
  IndustryQueryParams,
  IndustryOption,
} from "@/types/industry";
import { store } from "@/store";
import { logout } from "@/store/slices/authSlice";

const API_BASE_URL = import.meta.env.VITE_UTILS_API_URL;

// Crear instancia de axios con configuración base
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor para manejar errores de autenticación
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      store.dispatch(logout());
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

export const industryAPI = {
  /**
   * Obtener lista de industrias
   */
  getIndustries: async (
    params: IndustryQueryParams,
  ): Promise<IndustriesResponse> => {
    try {
      const queryParams = new URLSearchParams();

      // Parámetro obligatorio
      queryParams.append("slug", params.slug);

      // Parámetros opcionales
      if (params.is_active !== undefined) {
        queryParams.append("is_active", params.is_active.toString());
      }

      if (params.search) {
        queryParams.append("search", params.search);
      }

      console.log(
        "🔧 [IndustryService] Fetching industries from:",
        `${API_BASE_URL}/industries?${queryParams.toString()}`,
      );
      const response = await apiClient.get(
        `/industries?${queryParams.toString()}`,
      );

      console.log("🔧 [IndustryService] Response:", response.data);

      // Verificar estructura de respuesta
      if (response.data && response.data.success) {
        // Si la respuesta ya tiene el formato esperado
        return {
          success: true,
          data: response.data.data || [],
          count: response.data.count || 0,
        };
      } else if (
        response.data.statusCode === 200 &&
        response.data.body?.success
      ) {
        // Si la respuesta está anidada en body
        return {
          success: true,
          data: response.data.body.data || [],
          count: response.data.body.count || 0,
        };
      } else {
        throw new Error(
          response.data?.body?.message ||
            response.data?.message ||
            "Error al obtener industrias",
        );
      }
    } catch (error: any) {
      console.error("❌ [IndustryService] Error:", error);

      // Manejo de errores mejorado
      if (error.response) {
        const errorMessage =
          error.response.data?.body?.message ||
          error.response.data?.message ||
          "Error en el servidor";
        throw new Error(errorMessage);
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error(error.message || "Error al configurar la solicitud");
      }
    }
  },

  /**
   * Obtener industria por ID
   */
  getIndustryById: async (
    id: string,
    slug: string,
  ): Promise<IndustryResponse> => {
    try {
      const response = await apiClient.get(
        `/industries/${id}?slug=${slug}`,
      );

      const data = response.data;

      if (data.success || (data.statusCode === 200 && data.body?.success)) {
        return {
          success: true,
          data: data.data || data.body?.data,
        };
      } else {
        throw new Error(
          data.body?.message || data.message || "Error al obtener la industria",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(
          error.response.data?.body?.message ||
            error.response.data?.message ||
            "Error en el servidor",
        );
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error(error.message || "Error al configurar la solicitud");
      }
    }
  },

  /**
   * Crear nueva industria
   */
  createIndustry: async (
    industryData: CreateIndustryData,
  ): Promise<IndustryResponse> => {
    try {
      console.log("🔧 [IndustryService] Creating industry:", industryData);

      const response = await apiClient.post(`/industries`, industryData);

      console.log("🔧 [IndustryService] Create response:", response.data);

      const data = response.data;

      if (
        data.success ||
        ((data.statusCode === 200 || data.statusCode === 201) &&
          data.body?.success)
      ) {
        return {
          success: true,
          data: data.data || data.body?.data,
        };
      } else {
        const message = data.body?.message || data.message;

        if (
          message &&
          (message.toLowerCase().includes("creado") ||
            message.toLowerCase().includes("exitosamente") ||
            message.toLowerCase().includes("success"))
        ) {
          console.log(
            "⚠️ Mensaje indica éxito, retornando como exitoso:",
            message,
          );
          return {
            success: true,
            data: data.body?.data || data.data,
          };
        }

        throw new Error(message || "Error al crear la industria");
      }
    } catch (error: any) {
      console.error("❌ [IndustryService] Create error:", error);

      if (
        error.response?.status === 201 &&
        error.response.data?.body?.success
      ) {
        console.log("✅ Status 201 detectado como éxito");
        return {
          success: true,
          data: error.response.data.body.data,
        };
      }

      if (error.response) {
        const errorMessage =
          error.response.data?.body?.message ||
          error.response.data?.message ||
          error.response.data?.error ||
          "Error en el servidor";

        // Manejo específico de errores de validación
        if (error.response.status === 400 || error.response.status === 422) {
          throw new Error(`Error de validación: ${errorMessage}`);
        }

        throw new Error(errorMessage);
      } else if (error.request) {
        throw new Error(
          "No se pudo conectar con el servidor. Verifica tu conexión.",
        );
      } else {
        throw new Error(error.message || "Error al configurar la solicitud");
      }
    }
  },

  /**
   * Actualizar industria existente
   */
  updateIndustry: async (
    id: string,
    industryData: UpdateIndustryData,
  ): Promise<IndustryResponse> => {
    try {
      const response = await apiClient.put(
        `/industries/${id}`,
        industryData
      );

      const data = response.data;

      if (data.success || (data.statusCode === 200 && data.body?.success)) {
        return {
          success: true,
          data: data.data || data.body?.data,
        };
      } else {
        throw new Error(
          data.body?.message ||
            data.message ||
            "Error al actualizar la industria",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(
          error.response.data?.body?.message ||
            error.response.data?.message ||
            "Error en el servidor",
        );
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error(error.message || "Error al configurar la solicitud");
      }
    }
  },

  /**
   * Eliminar industria
   */
  deleteIndustry: async (id: string, slug: string): Promise<SimpleResponse> => {
    try {
      const response = await apiClient.delete(
        `/industries/${id}?slug=${slug}`,
      );

      const data = response.data;

      if (data.success || (data.statusCode === 200 && data.body?.success)) {
        return {
          success: true,
          message:
            data.body?.message ||
            data.message ||
            "Industria eliminada exitosamente",
        };
      } else {
        throw new Error(
          data.body?.message ||
            data.message ||
            "Error al eliminar la industria",
        );
      }
    } catch (error: any) {
      if (error.response) {
        throw new Error(
          error.response.data?.body?.message ||
            error.response.data?.message ||
            "Error en el servidor",
        );
      } else if (error.request) {
        throw new Error("No se pudo conectar con el servidor");
      } else {
        throw new Error(error.message || "Error al configurar la solicitud");
      }
    }
  },

  /**
   * Obtener industrias como opciones para selects
   */
  getIndustryOptions: async (slug: string): Promise<IndustryOption[]> => {
    try {
      const response = await industryAPI.getIndustries({
        slug,
        is_active: 1,
      });

      return response.data.map((industry: Industry) => ({
        value: industry.id,
        label: industry.name,
        is_default: industry.is_default,
      }));
    } catch (error) {
      console.error("Error al obtener opciones de industria:", error);
      return [];
    }
  },

  /**
   * Obtener industria por defecto
   */
  getDefaultIndustry: async (slug: string): Promise<Industry | null> => {
    try {
      const response = await industryAPI.getIndustries({
        slug,
        is_active: 1,
      });

      const defaultIndustry = response.data.find(
        (industry: Industry) => industry.is_default,
      );

      return defaultIndustry || null;
    } catch (error) {
      console.error("Error al obtener industria por defecto:", error);
      return null;
    }
  },

  /**
   * Buscar industrias por nombre
   */
  searchIndustries: async (
    slug: string,
    searchTerm: string,
  ): Promise<Industry[]> => {
    try {
      const response = await industryAPI.getIndustries({
        slug,
        is_active: 1,
        search: searchTerm,
      });

      return response.data;
    } catch (error) {
      console.error("Error al buscar industrias:", error);
      return [];
    }
  },

  /**
   * Verificar si existe una industria con el mismo nombre
   */
  checkIndustryExists: async (slug: string, name: string): Promise<boolean> => {
    try {
      const industries = await industryAPI.searchIndustries(slug, name);
      return industries.some(
        (industry: Industry) =>
          industry.name.toLowerCase() === name.toLowerCase(),
      );
    } catch (error) {
      console.error("Error al verificar industria:", error);
      return false;
    }
  },
};
